import * as React from 'react';
import { ErrorListProps } from '@rjsf/core';
import * as ThemeUI from 'theme-ui';
import { Notification } from 'components/Notification/Notification';
import { ErrorMessage } from 'components/form-elements/JsonForms/components/ErrorMessage';
import { useIntl } from 'lib/intl';
import { createPortal } from 'react-dom';
import { LocaleKey } from 'translations';
import { Box } from '../../../layout';

export const ERROR_LIST_CONTAINER_ID = 'ERROR_LIST_CONTAINER_ID';

/*
 * Generic, human-friendly sentence shown when an error message is not a known
 * translation key (e.g. a raw AJV message or a per-field key without a
 * translation). This guarantees every bullet reads as a proper sentence instead
 * of leaking technical jargon or a raw key.
 */
const FALLBACK_MESSAGE_ID = 'errors.summary.fallback';

export function ErrorList(props: ErrorListProps) {
  const intl = useIntl();
  const container = document.getElementById(ERROR_LIST_CONTAINER_ID);

  /*
   * Build the list of messages to display:
   *  1. Keep only errors that carry an actual message (empty / whitespace
   *     messages previously rendered as blank bullets).
   *  2. De-duplicate identical messages so the same problem isn't listed twice
   *     (the per-field inline errors already point at each affected field).
   */
  const messageIds = props.errors
    .map((error) => error.message)
    .filter(
      (message): message is string =>
        typeof message === 'string' && message.trim() !== '',
    )
    .filter((message, index, all) => all.indexOf(message) === index);

  if (messageIds.length === 0) {
    return null;
  }

  const fallbackMessage = intl.formatMessage({ id: FALLBACK_MESSAGE_ID });

  /*
   * Resolve a message id to a localized sentence. Known translation keys are
   * localized normally; anything else degrades gracefully to the generic
   * fallback sentence rather than showing the raw key / AJV text.
   */
  const resolveMessage = (messageId: string) =>
    (intl.messages[messageId] != null
      ? intl.formatMessage({ id: messageId as LocaleKey })
      : fallbackMessage);

  const element = (
    <ThemeUI.Box>
      <Notification severity="error">
        <ThemeUI.Box sx={{ ml: -7, mt: 2 }}>
          {messageIds.map((messageId) => (
            <ThemeUI.Box
              key={messageId}
              as="li"
              sx={{
                '&::marker': {
                  mr: 1,
                },
              }}
            >
              <Box as="span" sx={{ pl: '6px' }}>
                <ErrorMessage>{resolveMessage(messageId)}</ErrorMessage>
              </Box>
            </ThemeUI.Box>
          ))}
        </ThemeUI.Box>
      </Notification>
    </ThemeUI.Box>
  );

  return container ? createPortal(element, container) : element;
}
